// 057 — Atención al Adulto Mayor (Anexo 1 MSP, SNS-MSP/HCU-form.057/2021). Fase 7, Tanda 10: bloques B-N del papel
// (entorno social, motivo con informante, enfermedad actual, antecedentes E/F/G, situación funcional H, constantes I,
// revisión J, examen físico K, 6 diagnósticos con CIE y PRE/DEF + síndromes geriátricos L, pruebas M, tratamiento N).
// Campos y decisiones en `bloques057Datos.ts`. El bloque A lo resuelve `EncabezadoMspCampos`; el profesional (O) lo arma
// el servidor en el PDF firmado. Sin FHIR: `Par[]` en el PDF firmado.
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { AreaDictado } from './AreaDictado';
import { Bloque, CamposSimples } from './camposSimples';
import { MARCAS_PRE_DEF, vincularDescriba, type CampoSimple, type Valores } from './camposSimplesDatos';
import { SelectorDiagnosticoConMarca } from './MarcaDiagnostico';
import {
  CAMPOS_ALERTAS_057,
  CAMPOS_ANDRO_057,
  CAMPOS_BASICAS_057,
  CAMPOS_CLINICOS_057,
  CAMPOS_CONSTANTES_057,
  CAMPOS_ENFERMEDAD_057,
  CAMPOS_ENTORNO_057,
  CAMPOS_EXAMEN_057,
  CAMPOS_FAMILIARES_057,
  CAMPOS_FARMACOLOGICOS_057,
  CAMPOS_GENERALES_057,
  CAMPOS_GINECO_057,
  CAMPOS_HABITOS_057,
  CAMPOS_INSTRUMENTALES_057,
  CAMPOS_MOTIVO_057,
  CAMPOS_PRUEBAS_057,
  CAMPOS_REVISION_057,
  CAMPOS_SINDROMES_057,
  CAMPOS_TRATAMIENTO_057,
  estadoInicial057,
  numerosInvalidos057,
  paresDe057,
  type Estado057,
} from './bloques057Datos';

const CODIGO = '057';
const sinAreas = (campos: readonly CampoSimple[]): readonly CampoSimple[] => campos.filter((c) => c.tipo !== 'area');
const areas = (campos: readonly CampoSimple[]): readonly CampoSimple[] => campos.filter((c) => c.tipo === 'area');

type ClaveValores = Exclude<keyof Estado057, 'diagnosticos'>;

export function Formulario057({ subject, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const { guardando, error, guardar } = useGuardado();
  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const titulo = t('hub.formulario.057');
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, CODIGO, titulo);
  const [estado, setEstado] = useState<Estado057>(() => estadoInicial057());

  /** Casillas/selects/campos (`CamposSimples`) más las áreas de texto con dictado de un bloque de `Estado057`. */
  const bloque = (clave: ClaveValores, prefijo: string, lista: readonly CampoSimple[], columnas: 2 | 3 | 4, filas = 3): JSX.Element => {
    const valores: Valores = estado[clave];
    const claveDescriba = lista.find((c) => c.clave.endsWith('Describa'))?.clave;
    // "Describir anotando el número": al marcar una casilla se abre su renglón "N. " en el Describa del bloque.
    const poner = (v: Valores): void => setEstado({ ...estado, [clave]: claveDescriba ? vincularDescriba(valores, v, claveDescriba) : v });
    const simples = sinAreas(lista);
    return (
      <>
        {simples.length > 0 && <CamposSimples prefijoId={`f057-${prefijo}`} campos={simples} valores={valores} columnas={columnas} onChange={(v) => poner({ ...valores, ...v })} />}
        {areas(lista).map((c) => (
          <AreaDictado key={c.clave} id={`f057-${prefijo}-${c.clave}`} etiqueta={c.etiqueta} valor={valores[c.clave] ?? ''} filas={filas} onChange={(v) => poner({ ...valores, [c.clave]: v })} />
        ))}
      </>
    );
  };
  const sub = (titulo: string, contenido: JSX.Element): JSX.Element => (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-medium text-hce-ink">{titulo}</p>
      {contenido}
    </div>
  );

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const invalidos = numerosInvalidos057(estado);
      if (invalidos.length > 0) throw new Error(t('formularios.f057.numeroInvalido', { campos: invalidos.join(', ') }));
      onGuardado(paresDe057(estado), datosEncabezadoMsp);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <Bloque titulo={t('formularios.f057.entornoTitulo')}>{bloque('entorno', 'ent', CAMPOS_ENTORNO_057, 4)}</Bloque>
      <Bloque titulo={t('formularios.f057.motivoTitulo')}>{bloque('motivo', 'mot', CAMPOS_MOTIVO_057, 3)}</Bloque>
      <Bloque titulo={t('formularios.f057.enfermedadTitulo')}>{bloque('enfermedad', 'enf', CAMPOS_ENFERMEDAD_057, 2, 8)}</Bloque>

      <Bloque titulo={t('formularios.f057.antecedentesTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f057.antecedentesNota')}</p>
        {sub(t('formularios.f057.alertas'), bloque('alertas', 'ale', CAMPOS_ALERTAS_057, 3, 2))}
        {sub(t('formularios.f057.generales'), bloque('generales', 'gen', CAMPOS_GENERALES_057, 3, 2))}
        {sub(t('formularios.f057.habitos'), bloque('habitos', 'hab', CAMPOS_HABITOS_057, 4, 2))}
        {sub(t('formularios.f057.clinicos'), bloque('clinicos', 'cli', CAMPOS_CLINICOS_057, 3, 2))}
        {sub(t('formularios.f057.gineco'), bloque('gineco', 'gin', CAMPOS_GINECO_057, 3, 2))}
        {sub(t('formularios.f057.andro'), bloque('andro', 'and', CAMPOS_ANDRO_057, 2, 2))}
      </Bloque>

      <Bloque titulo={t('formularios.f057.farmacologicosTitulo')}>{bloque('farmacologicos', 'far', CAMPOS_FARMACOLOGICOS_057, 3, 2)}</Bloque>
      <Bloque titulo={t('formularios.f057.familiaresTitulo')}>{bloque('familiares', 'fam', CAMPOS_FAMILIARES_057, 3, 3)}</Bloque>

      <Bloque titulo={t('formularios.f057.funcionalTitulo')}>
        {sub(t('formularios.f057.basicas'), bloque('basicas', 'bas', CAMPOS_BASICAS_057, 4))}
        {sub(t('formularios.f057.instrumentales'), bloque('instrumentales', 'ins', CAMPOS_INSTRUMENTALES_057, 3))}
      </Bloque>

      <Bloque titulo={t('formularios.f057.constantesTitulo')}>{bloque('constantes', 'con', CAMPOS_CONSTANTES_057, 4)}</Bloque>

      <Bloque titulo={t('formularios.f057.revisionTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f057.marcarNota')}</p>
        {bloque('revision', 'rev', CAMPOS_REVISION_057, 4, 3)}
      </Bloque>
      <Bloque titulo={t('formularios.f057.examenTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f057.marcarNota')}</p>
        {bloque('examen', 'exa', CAMPOS_EXAMEN_057, 4, 6)}
      </Bloque>

      <Bloque titulo={t('formularios.f057.diagnosticoTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f057.diagnosticoNota')}</p>
        {estado.diagnosticos.map((d, i) => (
          <SelectorDiagnosticoConMarca
            key={i}
            label={`${t('formularios.f057.diagnostico')} ${i + 1}`}
            marcaLabel={t('formularios.f057.marcaDiagnostico')}
            marcas={MARCAS_PRE_DEF}
            value={d}
            onChange={(v) => setEstado({ ...estado, diagnosticos: estado.diagnosticos.map((x, j) => (j === i ? v : x)) })}
          />
        ))}
        {sub(t('formularios.f057.sindromes'), bloque('sindromes', 'sin', CAMPOS_SINDROMES_057, 4))}
      </Bloque>

      <Bloque titulo={t('formularios.f057.pruebasTitulo')}>{bloque('pruebas', 'pru', CAMPOS_PRUEBAS_057, 2, 3)}</Bloque>
      <Bloque titulo={t('formularios.f057.tratamientoTitulo')}>{bloque('tratamiento', 'tra', CAMPOS_TRATAMIENTO_057, 2, 8)}</Bloque>

      <Bloque titulo={t('formularios.f057.profesionalTitulo')}>
        <p className="text-sm text-hce-muted">{t('formularios.profesionalNotaCodigo')}</p>
      </Bloque>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
